function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useRef, useEffect } from 'react';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import Button from 'react-bootstrap/Button';
import Overlay from "react-bootstrap/Overlay";
import Tooltip from "react-bootstrap/Tooltip";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { createEditor, Editor, Transforms, Text, Element as SlateElement, Node, Range } from 'slate';
import { Slate, Editable, ReactEditor, withReact } from 'slate-react';
import { withHistory } from 'slate-history';
import { Content, Element, no_paragraph_tags, void_tags } from '../DataStructures';
import ErrorBoundary from '../ErrorBoundary';
import InteractiveRenderer from '../InteractiveRenderer';
import { jsx } from 'slate-hyperscript';
import Wrapper from '../Wrapper';
import { getRawText } from '../util';
import { MathJax } from "better-react-mathjax";
import e from '../ComponentLibrary';

// Set to true to debug this file
const DEBUGALL = false;
const DEBUG = DEBUGALL || false;
const DEBUGPERF = DEBUGALL || false;
const DEBUGNORM = DEBUGALL || false;
const inlineElements = ['MathJax', 'TaskPassageClick.Click', 'TaskPassageReveal.Passage', 'a', 'abbr', 'acronym', 'audio', 'b', 'bdi', 'bdo', 'big', 'br', 'button', 'canvas', 'cite', 'code', 'data', 'datalist', 'del', 'dfn', 'em', 'embed', 'i', 'iframe', 'img', 'input', 'ins', 'kbd', 'label', 'map', 'mark', 'meter', 'noscript', 'object', 'output', 'picture', 'progress', 'q', 'ruby', 's', 'samp', 'script', 'select', 'slot', 'small', 'span', 'strong', 'sub', 'sup', 'svg', 'template', 'textarea', 'time', 'u', 'tt', 'var', 'video', 'wbr'];
const allowedTags = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'P', 'DIV', 'OL', 'UL', 'LI', 'A', 'B', 'STRONG', 'BR', 'I', 'EM', 'CODE', 'SMALL', 'SPAN', 'SUB', 'SUP', 'U', 'SVG', 'MATH', 'DEL', 'CAPTION', 'COL', 'COLGROUP', 'TABLE', 'TBODY', 'TD', 'TFOOT', 'TH', 'THEAD', 'TR', 'DETAILS', 'SUMMARY'];
const editableComponents = ['TaskPassageClick.Click', 'TaskPassageReveal.Passage'];
const nonPrintingStartRegex = /^[\p{Z}\p{C}\u200B\s\n]+/u;
const nonPrintingEndRegex = /[\p{Z}\p{C}\u200B\s\n]+$/u;
const Leaf = props => {
  var _props$children, _props$children3, _props$children4;
  if ((_props$children = props.children) !== null && _props$children !== void 0 && _props$children.length) {
    for (const child of props.children) {
      DEBUGALL && console.log("slate Leaf child", child);
    }
  } else {
    var _props$children2;
    DEBUGALL && console.log("slate children non-iterable", (_props$children2 = props.children) === null || _props$children2 === void 0 ? void 0 : _props$children2.text);
  }
  return /*#__PURE__*/React.createElement("span", props.attributes, (_props$children3 = props.children) !== null && _props$children3 !== void 0 && _props$children3.hasOwnProperty("text") ? (_props$children4 = props.children) === null || _props$children4 === void 0 ? void 0 : _props$children4.text : props.children);
};
const parseRem = string => {
  return parseInt(string.substring(0, string.indexOf("rem")));
};
const CustomEditor = {
  ...Editor,
  isInline(editor, element) {
    return inlineElements.includes(element.tagName);
  },
  isVoid(editor, node) {
    if (node.type === "Component") {
      if (editableComponents.includes(node.tagName)) {
        return false;
      }
      return true;
    }
  },
  isTagActive(editor, tag) {
    const [match] = CustomEditor.nodes(editor, {
      match: n => {
        return n.tagName === tag;
      }
    });
    return !!match;
  },
  getActiveTag(editor, tag) {
    const [match] = CustomEditor.nodes(editor, {
      match: n => {
        return n.tagName === tag;
      }
    });
    return match;
  },
  moveSelectionDepth(editor, depthChange) {
    DEBUGALL && console.log("RichText moveSelectionDepth");
    let selectionPath = editor.selection.anchor.path.slice();
    if (depthChange < 0) {
      selectionPath.length -= depthChange;
    } else {
      for (let i = 0; i < depthChange; ++i) {
        selectionPath.push(0);
      }
    }
    return {
      anchor: {
        path: selectionPath,
        offset: 0
      },
      focus: {
        path: selectionPath,
        offset: 0
      }
    };
  },
  moveSelection(editor, blockChange) {
    DEBUGALL && console.log("RichText moveSelection");
    let selectionPath = editor.selection.anchor.path.slice();
    selectionPath[selectionPath.length - 1] += blockChange;
    return {
      anchor: {
        path: selectionPath,
        offset: 0
      },
      focus: {
        path: selectionPath,
        offset: 0
      }
    };
  },
  normalizeNode(editor, entry, props) {
    const [node, path] = entry;
    // DEBUGNORM && console.log("slate normalize editor.normalizeNode")
    // If the node is an element and the node's children have adjacent strings, they are combined.
    if (SlateElement.isElement(node)) {
      let prevChild;
      for (const [child, childPath] of Node.children(editor, path)) {
        if (prevChild && Text.isText(prevChild) && Text.isText(child)) {
          Transforms.mergeNodes(editor, {
            at: childPath
          });
          DEBUGNORM && console.log("slate normalize If the node is an element and the node's children have adjacent strings, they are combined.");
          return true;
        }
        prevChild = child;
      }
    }

    // If the node is identical to its only child, they are merged.
    if (SlateElement.isElement(node)) {
      let nodechildren = [...Node.children(editor, path)];
      DEBUGNORM && console.log("slate normalize iselement, children", nodechildren);
      if (nodechildren.length === 3) {
        if (Text.isText(nodechildren[0][0]) && Text.isText(nodechildren[2][0]) && nodechildren[0][0].text === '' && nodechildren[2][0].text === '') {
          let noderest, childrest;
          if (true) {
            let {
              children,
              ...rest
            } = node;
            noderest = rest;
          }
          if (true) {
            let {
              children,
              ...rest
            } = nodechildren[1][0];
            childrest = rest;
          }
          let json1 = JSON.stringify(noderest);
          let json2 = JSON.stringify(childrest);
          DEBUGNORM && console.log("slate normalize children looks right.", json1, json2);
          if (json1 === json2) {
            DEBUGNORM && console.log("slate normalize json equal");
            Transforms.unwrapNodes(editor, {
              at: path
            });
            DEBUGNORM && console.log("slate normalize If the node is identical to its only child, they are merged.");
            return true;
          }
        }
      }
    }

    // If the node is an element, adjacent inline elements with identical properties are merged
    if (SlateElement.isElement(node)) {
      let prevChild;
      for (const [child, childPath] of Node.children(editor, path)) {
        if (prevChild && SlateElement.isElement(prevChild) && SlateElement.isElement(child) && prevChild.tagName === child.tagName && inlineElements.includes(prevChild.tagName) && JSON.stringify(prevChild.props) === JSON.stringify(child.props)) {
          Transforms.mergeNodes(editor, {
            at: childPath
          });
          DEBUGNORM && console.log("slate normalize If the node is an element, adjacent inline elements with identical properties are merged");
          return true;
        }
        prevChild = child;
      }
    }

    // If the node is an element and inlineOnly is true, change it to 'wrapper' (to be removed in DataStructure). 
    if (SlateElement.isElement(node) && props.inlineOnly && node.tagName !== 'wrapper') {
      Transforms.setNodes(editor, {
        tagName: 'wrapper'
      });
      DEBUGNORM && console.log("slate normalize If the node is an element and inlineOnly is true, change it to 'wrapper' (to be removed in DataStructure).", JSON.stringify(node));
      return true;
    }

    // If the element is a paragraph, split any double breaks into paragraphs.
    if (SlateElement.isElement(node) && node.tagName === 'p') {
      let lastChild, lastChildPath;
      for (const [child, childPath] of Node.children(editor, path)) {
        if (SlateElement.isElement(child) && child.tagName === 'br') {
          Transforms.splitNodes(editor, {
            at: childPath
          });
          DEBUGNORM && console.log("slate normalize If the element is a paragraph, split any double breaks into paragraphs.");
          return true;
        }
        [lastChild, lastChildPath] = [child, childPath];
      }
    }

    // Remove inline elements with no text, unless they were added as part of Slate normalization.
    if (SlateElement.isElement(node) && inlineElements.includes(node.tagName)) {
      let text = getRawText(serialize(node));
      if (text.length === 0 && node.tagName !== "MathJax") {
        if (true || node.tagName === "TaskPassageClick.Click") {
          Transforms.removeNodes(editor, {
            at: path
          });
          DEBUGNORM && console.log("slate normalize Remove inline elements with no text.", node, text);
          return true;
        }
        //return true
      } // Causes an infinite loop
    }

    // If the element is a paragraph, trim any trailing or leading newlines or invisible characters.
    if (SlateElement.isElement(node) && node.tagName === 'p') {
      let traverseFirst = (editor, path) => {
        for (const [child, childPath] of Node.children(editor, path)) {
          if (Text.isText(child)) {
            let match = child.text.match(nonPrintingStartRegex);
            let ntrim = match && match[0] && match[0].length || 0;
            //DEBUGNORM && console.log("slate normalize p not element", child, "can trim", ntrim)
            if (ntrim > 0 && false) {
              //DEBUGNORM && console.log("slate normalize p trimming", ntrim)
              Transforms.delete(editor, {
                at: {
                  anchor: {
                    path: childPath,
                    offset: 0
                  },
                  focus: {
                    path: childPath,
                    offset: ntrim
                  }
                }
              });
              return true;
            } else {
              //DEBUGNORM && console.log("slate normalize p did not trim", child.text)
            }
            return false;
          } else {
            return traverseFirst(editor, childPath);
          }
        }
      };
      if (traverseFirst(editor, path)) {
        DEBUGNORM && console.log("slate normalize If the element is a paragraph, trim any trailing or leading newlines or invisible characters.");
        return true;
      }
      let traverseLast = (editor, path) => {
        let children = Node.children(editor, path);
        // DEBUGNORM && console.log("slate normalize traverseLast children", children)
        let lastChild, lastChildPath;
        for (const [child, childPath] of Node.children(editor, path)) {
          [lastChild, lastChildPath] = [child, childPath];
        }
        // DEBUGNORM && console.log("slate normalize traverseLast", lastChild, lastChildPath)
        if (Text.isText(lastChild)) {
          let match = lastChild.text.match(nonPrintingEndRegex);
          match = match && match[0] || "";
          let ntrim = (match.length || 0) - 1; // offset by 1 to allow spaces
          // DEBUGNORM && console.log("slate normalize p not element", lastChild, "can trim", ntrim)
          if (ntrim > 0 && false) {
            DEBUGNORM && console.log("slate normalize p trimming", ntrim, lastChild.text.substring(lastChild.text.length - ntrim, lastChild.text.length));
            Transforms.delete(editor, {
              at: {
                anchor: {
                  path: lastChildPath,
                  offset: lastChild.text.length - ntrim
                },
                focus: {
                  path: lastChildPath,
                  offset: lastChild.text.length
                }
              }
            });
            return true;
          } else {
            // DEBUGNORM && console.log("slate normalize p did not trim", lastChild.text)
          }
          return false;
        } else {
          return traverseLast(editor, lastChildPath);
        }
      };
      if (traverseLast(editor, path)) {
        DEBUGNORM && console.log("slate normalize If the element is a paragraph, trim any trailing or leading newlines or invisible characters.");
        return true;
      }
    }

    // If the element is a paragraph, ensure its children are valid (destructively).
    if (SlateElement.isElement(node) && no_paragraph_tags.includes(node.tagName)) {
      for (const [child, childPath] of Node.children(editor, path)) {
        if (SlateElement.isElement(child) && !editor.isInline(child)) {
          DEBUGNORM && console.log("slate normalize If the element is a paragraph, ensure its children are valid (destructively).");
          Transforms.unwrapNodes(editor, {
            at: childPath
          });
          return true;
        }
      }
    }
  },
  toggleTwoColumn(editor, tag, type) {
    if (!editor.selection) {
      DEBUGALL && console.log("slate toggleTag selection was", editor.selection);
      return;
    }
    const isActive = CustomEditor.isTagActive(editor, tag);
    const isCollapsed = Range.isCollapsed(editor.selection);
    DEBUGALL && console.log("slate toggleTwoColumn", tag, type, isActive, isCollapsed);
    if (!isActive) {
      if (isCollapsed) {}
    }
  },
  toggleTag(editor, tag, type) {
    if (!editor.selection) {
      DEBUGALL && console.log("slate toggleTag selection was", editor.selection);
      return;
    }
    const isActive = CustomEditor.isTagActive(editor, tag);
    const isCollapsed = Range.isCollapsed(editor.selection);
    DEBUGALL && console.log("slate toggleTag", tag, type, isActive, isCollapsed);
    if (!isActive) {
      if (isCollapsed) {
        DEBUGALL && console.log("slate toggleTag adding in place");
        if (inlineElements.includes(tag)) {
          // let moved = CustomEditor.moveSelection(editor, 1)
          // Transforms.insertNodes(
          //   editor,
          //   [{ type: type ?? 'Element', tagName: tag, children: [{ text: undefined }] }, { text: undefined }],
          //   { select: true }
          // )
          // Transforms.select(
          //   editor,
          //   moved
          // )
        } else {
          Transforms.wrapNodes(editor, {
            type: type !== null && type !== void 0 ? type : 'Element',
            tagName: tag
          }, {
            mode: "highest"
          });
        }
      } else {
        DEBUGALL && console.log("slate toggleTag adding wrapped");
        Transforms.wrapNodes(editor, {
          type: type !== null && type !== void 0 ? type : 'Element',
          tagName: tag
        }, {
          split: true
        });
      }
    } else {
      if (isCollapsed) {
        DEBUGALL && console.log("slate toggleTag clearing for futher text");
        if (inlineElements.includes(tag)) {
          Transforms.insertFragment(editor, [{
            text: ''
          }]);
        } else {
          Transforms.setNodes(editor, {
            type: 'Element',
            tagName: 'p'
          }, {
            match: node => node.tagName === tag
          });
        }
      } else {
        DEBUGALL && console.log("slate toggleTag unwrapping");
        if (inlineElements.includes(tag)) {
          Transforms.unwrapNodes(editor, {
            match: node => node.tagName === tag,
            split: true
          });
        } else {
          Transforms.setNodes(editor, {
            type: 'Element',
            tagName: 'p'
          }, {
            match: node => node.tagName === tag
          });
        }
      }
    }
  },
  renderElement(editor, props) {
    let child = props.element;
    if (props.element.type === "Element") {
      if (child.tagName === "wrapper") {
        return props.children;
      }
      return /*#__PURE__*/React.createElement(child.tagName || "mark", {
        ...props.attributes,
        ...child.props
      }, ...props.children);
    } else if (props.element.type === "Component") {
      if (props.onClickNonEditable) {
        return /*#__PURE__*/React.createElement(Wrapper, _extends({}, props.attributes, {
          "data-editor-component": true,
          style: {
            userSelect: 'none'
          },
          contentEditable: false,
          onClick: e => {
            e.stopPropagation();
            e.preventDefault();
            props.onClickNonEditable();
          }
        }), /*#__PURE__*/React.createElement(InteractiveRenderer, {
          data: serialize(child)
        }));
      } else if (props.onClickModifyFormula) {
        /*#__PURE__*/React.createElement(Wrapper, _extends({}, props.attributes, {
          "data-editor-component": true,
          style: {
            userSelect: 'none'
          },
          contentEditable: false,
          onClick: e => {
            e.stopPropagation();
            e.preventDefault();
            props.onClickModifyFormula();
          }
        }), /*#__PURE__*/React.createElement(InteractiveRenderer, {
          data: serialize(child)
        }));
      } else {
        DEBUGALL && console.log("RichText", child.tagName, props, this, editor);
        return /*#__PURE__*/React.createElement(InteractiveRenderer, {
          data: serialize(child),
          attributes: props.attributes
        }, props.children);
      }
    } else {
      DEBUGALL && console.log("slate renderElement neither", props);
      return props.children;
    }
  },
  renderLeaf(props) {
    DEBUGALL && console.log("slate renderLeaf", props);
    return /*#__PURE__*/React.createElement(Leaf, props);
  }
};
export function preprocessHtml(html) {
  let start = html.indexOf("<!--StartFragment-->");
  let end = html.indexOf("<!--EndFragment-->");
  if (start !== -1 && end !== -1) {
    html = html.substring(start + "<!--StartFragment-->".length, end);
  }
  html = html.replace(/<br><br>/g, "<p>");
  html = html.replace(/\n/g, "");
  return html;
}
export const deserializeHtml = el => {
  DEBUGALL && console.log("deserializeHtml", el);
  if (el.nodeType === 3) {
    return el.textContent;
  } else if (el.nodeType !== 1) {
    return null;
  } else if (el.nodeName === 'BR') {
    return '\n';
  }
  let {
    nodeName
  } = el;
  let parent = el;
  DEBUGALL && console.log("nodeName", nodeName, el.nodeName);

  //if (nodeName === 'B') {
  //  //DEBUG && console.log("RichText lookForBoldedText result", lookForBoldedText(el))
  //  if(el.style["font-weight"] === "normal")
  //    nodeName = 'SPAN'
  //}
  //
  //if(nodeName === 'SPAN') {
  //  if(el.style["font-weight"] === "700") {
  //    delete el.style["font-weight"]
  //    console.log("AFTER", el.style["font-weight"])
  //    return deserializeHtml(jsx('element', 
  //      {type: 'Element',
  //      tagName: 'b',
  //    }, [el]))
  //  }
  //
  //  if(el.style["font-style"] === "italic") {
  //    delete el.style["font-style"] 
  //    return deserializeHtml(jsx('element', 
  //    {type: 'Element',
  //    tagName: 'i',
  //    }, [el]))
  //  }
  //}

  if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
    parent = el.childNodes[0];
  }
  const children = [];
  const childNodes = Array.from(parent.childNodes);
  for (const el of childNodes) {
    children.push(deserializeHtml(el));
  }
  if (!allowedTags.includes(el.nodeName)) {
    return jsx('fragment', {}, children);
  }
  return jsx('element', {
    type: 'Element',
    tagName: nodeName.toLowerCase()
  }, children);
};
const withHtml = editor => {
  const {
    insertData
  } = editor;
  editor.insertData = data => {
    let html = data.getData('text/html');
    if (html) {
      editor.lastHTMLPaste = html;
      DEBUG && console.log("RichText html1", html);
      html = preprocessHtml(html);
      DEBUGALL && console.log("html2", html);
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fragment = deserializeHtml(parsed.body);
      Transforms.insertFragment(editor, fragment);
      return;
    }
    insertData(data);
  };
  return editor;
};

// Serialize Slate into DataStructure
export const serialize = node => {
  //if (node.tagName === 'wrapper') {
  //  return node.children
  //}
  //return node
  //DEBUGALL && console.log("slate serialize", node)
  if (node.toJS) {
    node = node.toJS();
    console.log("node.toJS");
  }
  if (Text.isText(node) || node.hasOwnProperty("text") || typeof node === "string") {
    return node.text || node || '';
  }
  if (node.tagName === 'wrapper') {
    return serializeArray(node.children);
  }
  if (typeof node !== "object") {
    return node;
  }
  let {
    ...rest
  } = node;
  DEBUGNORM && console.log("@item", rest);
  for (const key of Object.keys(rest)) {
    DEBUGNORM && console.log("@key", key, rest[key]);
    if (Object.hasOwn(rest, key) && key !== undefined) {
      if (rest[key] && rest[key].map) {
        rest[key] = serializeArray(rest[key]);
      } else if (rest[key]) {
        rest[key] = serialize(rest[key]);
      }
    }
  }
  return {
    ...rest
  };
};
export const serializeArray = arr => {
  let newArr = new Content();
  for (let i = 0; i < arr.length; ++i) {
    let serialized = serialize(arr[i]);
    if (serialized.map) {
      for (let j = 0; j < serialized.length; ++j) {
        newArr.push(serialized[j]);
      }
    } else {
      newArr.push(serialized);
    }
  }
  return newArr;
};

// Deserialize DataStructure into Slate
const deserialize = (node, inlineOnly) => {
  //DEBUGALL && console.log("slate deserialize", node)
  if (typeof node === "string") {
    return {
      text: node
    };
  }
  if (node.hasOwnProperty("text")) {
    return node;
  }
  if (node.map) {
    // DEBUGALL && console.log("slate deserialize wrapping")
    node = new Element({
      tagName: inlineOnly ? 'wrapper' : 'p',
      children: node
    });
  }
  let {
    children,
    tagName,
    ...rest
  } = node;
  if (children) {
    children = children.map(deserialize);
  } else {
    console.log("no children", node);
  }
  if (children.length === 0) {
    children = [{
      text: ''
    }];
  }
  return jsx('element', {
    tagName,
    ...rest
  }, children);
};
function DebugButton({
  editor,
  propValue,
  theRef,
  tabIndex
}) {
  let ref = React.useRef(null);
  // let buttonRef = React.useRef(null)
  let [show, setShow] = React.useState(false);
  let timer = React.useRef();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
    ref: theRef,
    tabIndex: tabIndex,
    size: "sm",
    variant: "dark",
    className: `p-0 ml-1 my-1`,
    id: "debug",
    onClick: e => {
      ref.current.select();
      document.execCommand("copy");
      setShow(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        setShow(false);
      }, 1000);
    }
  }, /*#__PURE__*/React.createElement("i", {
    className: `bi bi-bug-fill`
  })), /*#__PURE__*/React.createElement(Overlay, {
    target: theRef.current,
    show: show,
    placement: "right"
  }, props => /*#__PURE__*/React.createElement(Tooltip, props, "Copied to clipboard")), /*#__PURE__*/React.createElement("textarea", {
    className: "sr-only",
    "aria-hidden": "true",
    tabIndex: -1,
    ref: ref,
    readOnly: true,
    value: `Last Paste\n====\n\`\`\`\n${editor.lastHTMLPaste}\n\`\`\`\n\nCurrent value\n====\n\`\`\`\n${JSON.stringify(propValue)}\n\`\`\``
  }));
}
function ToolbarButton({
  tagName,
  type,
  icon,
  className,
  isActive,
  onClick,
  editor,
  inlineOnly,
  tabIndex,
  theRef,
  ...rest
}) {
  if (inlineOnly && !inlineElements.includes(tagName)) {
    return null;
  }
  if (typeof isActive === "function") {
    isActive = isActive();
  }
  if (typeof isActive === "undefined") {
    const [match] = CustomEditor.nodes(editor, {
      match: n => n.tagName === tagName
    });
    isActive = !!match;
  }
  return /*#__PURE__*/React.createElement(Button, _extends({
    tabIndex: tabIndex,
    size: "sm",
    variant: isActive ? "primary" : "dark",
    className: `p-0 ml-1 my-1 ${className || ""}`,
    id: tagName,
    ref: theRef,
    onClick: onClick || (e => {
      DEBUGALL && console.log("RichText ToolbarButton click");
      ReactEditor.focus(editor);
      CustomEditor.toggleTag(editor, tagName, type);
    })
  }, rest), /*#__PURE__*/React.createElement("i", {
    className: `bi bi-${icon}`
  }));
}
const MathJaxModal = props => {
  const [formula, setFormula] = useState("");
  return /*#__PURE__*/React.createElement(Modal, {
    show: props.showModal,
    onHide: props.handleClose
  }, /*#__PURE__*/React.createElement(Modal.Header, {
    closeButton: true
  }, /*#__PURE__*/React.createElement(Modal.Title, null, "Equation Builder")), /*#__PURE__*/React.createElement(Modal.Body, null, /*#__PURE__*/React.createElement(Form.Label, null, "Enter MathJax formula: "), /*#__PURE__*/React.createElement(Form.Control, {
    as: "textarea",
    value: formula,
    onChange: e => {
      setFormula(e.target.value);
    }
  }), /*#__PURE__*/React.createElement(Form.Label, null, "Preview: "), /*#__PURE__*/React.createElement("div", null, formula !== "" && /*#__PURE__*/React.createElement(MathJax, {
    renderMode: "pre",
    inline: true,
    dynamic: true,
    text: formula,
    typesettingOptions: {
      fn: "tex2chtml"
    }
  }, "ab"))), /*#__PURE__*/React.createElement(Modal.Footer, null, /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    onClick: props.handleClose
  }, "Close"), /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    onClick: () => {
      props.handleClose();
      props.handleModalSave(formula);
    }
  }, "Save Changes")));
};
export default function RichText(props) {
  var _props$toolbar, _props$value, _props$toolbar2, _props$toolbar3, _props$toolbar4, _props$toolbar5;
  // Generate toolbar button refs
  const toolbarRefs = useRef([]);
  toolbarRefs.current = Array(20 + (((_props$toolbar = props.toolbar) === null || _props$toolbar === void 0 ? void 0 : _props$toolbar.length) || 0)).fill(0).map((ref, i) => toolbarRefs.current[i] = /*#__PURE__*/React.createRef());
  // toolbar cursor
  const [toolbarCursor, setToolbarCursor] = useState(0);
  // when left or right arrow pressed, change the toolbarCursor
  const handleToolbarKeyDown = e => {
    if (e.key === 'ArrowLeft' && 0 < toolbarCursor) {
      let v = toolbarCursor - 1;
      setToolbarCursor(v);
      toolbarRefs.current[v].current.focus();
    }
    if (e.key === 'ArrowRight' && toolbarCursor < toolbarRefs.current.length - 1) {
      let v = toolbarCursor + 1;
      setToolbarCursor(v);
      toolbarRefs.current[v].current.focus();
    }
  };
  const [showModal, setShowModal] = useState(false);
  const handleClose = () => setShowModal(false);
  const handleShow = () => setShowModal(true);
  const handleModalSave = formula => {
    console.log("HANDLE", formula);
    const MathNode = [{
      type: 'Component',
      tagName: 'MathJax',
      props: {
        renderMode: "pre",
        inline: true,
        dynamic: true,
        style: {
          userSelect: "none"
        },
        contentEditable: false,
        text: formula,
        typesettingOptions: {
          fn: "tex2chtml"
        },
        className: "MathJaxFormula",
        onClickModifyFormula: () => {
          handleShow();
        }
      },
      children: [{
        text: ""
      }]
    }];
    Transforms.insertNodes(editor, MathNode);
  };
  const editor = React.useMemo(() => {
    let a = createEditor();
    a.DEBUGNORM = DEBUGNORM;
    let e = withHtml(withReact(withHistory(a)));
    e.DEBUGNORM = DEBUGNORM;
    return e;
  }, []);
  if (props.editorRef) {
    props.editorRef.current = editor;
  }
  if (props.customEditorRef) {
    props.customEditorRef.current = CustomEditor;
  }
  const {
    isInline,
    isVoid,
    normalizeNode,
    insertBreak
  } = editor;
  editor.isInline = element => {
    return CustomEditor.isInline(editor, element);
  };
  editor.isVoid = node => {
    var _CustomEditor$isVoid;
    return (_CustomEditor$isVoid = CustomEditor.isVoid(editor, node)) !== null && _CustomEditor$isVoid !== void 0 ? _CustomEditor$isVoid : isVoid(node);
  };
  editor.DEBUGNORM = DEBUGNORM;
  editor.normalizeNode = entry => {
    // Fall back to the original `normalizeNode` to enforce other constraints.
    if (CustomEditor.normalizeNode(editor, entry, props) === undefined) {
      //DEBUGNORM && console.log("slate normalized custom + default")
      editor.normalizeNode.orig(entry);
    } else {
      DEBUGNORM && console.log("slate normalized custom");
    }
  };
  editor.normalizeNode.orig = normalizeNode.orig || normalizeNode;
  editor.insertBreak = () => {
    insertBreak(editor);
    /*Transforms.setNodes(editor, {
      tagName: 'p'
    })*/
  };

  // Slate has this to say about bare text:

  // Only allow block nodes in the top-level children and parent blocks
  // that only contain block nodes. Similarly, only allow inline nodes in
  // other inline nodes, or parent blocks that only contain inlines and
  // text.

  // See: https://docs.slatejs.org/concepts/11-normalizing#built-in-constraints
  if (!((_props$value = props.value) !== null && _props$value !== void 0 && _props$value.map)) {
    console.warn("slate RichText: expected a list of elements, got", JSON.stringify(props.value));
  }
  const propValue = React.useMemo(() => {
    let v = props.value ? props.value.map ? props.value : new Content(props.value) : new Content();

    // Ensure that there are only block level elements at the top level.
    let needsWrapper = true;
    for (const c of v) {
      if (editor.isInline(c) || c.hasOwnProperty("text") || typeof c === "string") {
        needsWrapper = true;
        break;
      } else {
        needsWrapper = false;
      }
    }
    if (needsWrapper) {
      v = new Content(deserialize(v, props.inlineOnly));
    } else {
      v = v.map(deserialize);
    }
    DEBUGALL && console.log("slate needsWrapper", needsWrapper);
    function checkChildren(v, path) {
      if (v.children) {
        for (const key in v.children) {
          checkChildren(v.children[key], path + "." + key);
        }
      } else {
        console.log("no children:", path, v);
      }
    }
    //console.log("v", v)
    //checkChildren(v, "")
    editor.children = v;
    CustomEditor.normalize(editor, {
      force: true
    });
    return editor.children;
  }, [editor, props.value]);
  React.useEffect(() => {
    DEBUGPERF && performance.mark("RichText onChange comparison a");
    let serialized = serializeArray(propValue);
    DEBUGPERF && performance.mark("RichText onChange comparison b");
    DEBUGPERF && performance.measure("RichText onChange comparison", "RichText onChange comparison a", "RichText onChange comparison b");
    if (JSON.stringify(serialized) !== JSON.stringify(props.value)) {
      props.onChange(serialized);
    }
  }, [propValue, props.value]);
  DEBUGALL && console.log("slate propValue", propValue);

  /*return <code>
    {JSON.stringify(serializeArray(propValue))}
  </code>*/
  const alignText = alignment => {
    ReactEditor.focus(editor);
    let [match] = CustomEditor.nodes(editor, {
      match: n => {
        var _n$props, _n$props$style;
        return n.tagName === 'p' && ((_n$props = n.props) === null || _n$props === void 0 ? void 0 : (_n$props$style = _n$props.style) === null || _n$props$style === void 0 ? void 0 : _n$props$style.textAlign) === alignment;
      }
    });
    if (!!match) {
      // if there is a p with the same alignment, remove the alignment
      Transforms.setNodes(editor, {
        props: {
          style: {
            textAlign: null
          }
        }
      }, {
        match: n => {
          var _n$props2, _n$props2$style;
          return n.tagName === 'p' && ((_n$props2 = n.props) === null || _n$props2 === void 0 ? void 0 : (_n$props2$style = _n$props2.style) === null || _n$props2$style === void 0 ? void 0 : _n$props2$style.textAlign) === alignment;
        }
      }, {});
      return;
    }
    [match] = CustomEditor.nodes(editor, {
      match: n => {
        var _n$props3, _n$props3$style;
        return n.tagName === 'p' && ((_n$props3 = n.props) === null || _n$props3 === void 0 ? void 0 : (_n$props3$style = _n$props3.style) === null || _n$props3$style === void 0 ? void 0 : _n$props3$style.textAlign) !== alignment;
      }
    });
    if (!!match) {
      // if there is a p with a different alignment, set the right alignment
      Transforms.setNodes(editor, {
        props: {
          style: {
            textAlign: alignment
          }
        }
      }, {
        match: n => {
          var _n$props4, _n$props4$style;
          return n.tagName === 'p' && ((_n$props4 = n.props) === null || _n$props4 === void 0 ? void 0 : (_n$props4$style = _n$props4.style) === null || _n$props4$style === void 0 ? void 0 : _n$props4$style.textAlign) !== alignment;
        }
      }, {});
    }
  };
  const applyFontVariant = variant => {
    ReactEditor.focus(editor);
    const [isActive] = CustomEditor.nodes(editor, {
      match: n => {
        var _n$props5, _n$props5$style;
        return n.tagName === "span" && (n === null || n === void 0 ? void 0 : (_n$props5 = n.props) === null || _n$props5 === void 0 ? void 0 : (_n$props5$style = _n$props5.style) === null || _n$props5$style === void 0 ? void 0 : _n$props5$style.fontVariant) === variant;
      }
    });
    const isCollapsed = Range.isCollapsed(editor.selection);
    if (isCollapsed) {
      return;
    }
    if (!isActive) {
      Transforms.wrapNodes(editor, {
        type: 'Element',
        tagName: "span",
        props: {
          style: {
            fontVariant: variant
          }
        }
      }, {
        split: true
      });
    } else {
      Transforms.unwrapNodes(editor, {
        match: node => {
          var _node$props, _node$props$style;
          return ((_node$props = node.props) === null || _node$props === void 0 ? void 0 : (_node$props$style = _node$props.style) === null || _node$props$style === void 0 ? void 0 : _node$props$style.fontVariant) === variant;
        }
      });
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "richtext",
    onClick: e => {
      e.stopPropagation();
    },
    onMouseDown: e => {
      e.stopPropagation();
    }
  }, /*#__PURE__*/React.createElement(ErrorBoundary, {
    lastPaste: editor.lastHTMLPaste,
    slateValue: propValue
  }, /*#__PURE__*/React.createElement(Slate, {
    editor: editor,
    value: propValue,
    onChange: newValue => {
      if (props.onChange) {
        DEBUGPERF && performance.mark("RichText Slate onChange comparison a");
        let serializedValue = serializeArray(newValue);
        let snew = JSON.stringify(serializedValue);
        let sold = JSON.stringify(propValue);
        DEBUGPERF && performance.mark("RichText Slate onChange comparison b");
        DEBUGPERF && performance.measure("RichText Slate onChange comparison", "RichText Slate onChange comparison a", "RichText Slate onChange comparison b");
        if (snew !== sold) {
          DEBUGNORM && console.log("RichText Slate onChange");
          if (getRawText(serializedValue).length === 0) {
            props.onChange();
          } else {
            props.onChange(serializedValue);
          }
        }
      }
    }
  }, /*#__PURE__*/React.createElement(ButtonToolbar, {
    className: "richtext-toolbar",
    role: "toolbar",
    tabIndex: -1,
    onKeyDown: handleToolbarKeyDown
  }, /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[0],
    tabIndex: toolbarCursor == 0 ? 0 : -1,
    "aria-selected": toolbarCursor == 0 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "b",
    icon: "type-bold",
    title: "Bold"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[1],
    tabIndex: toolbarCursor == 1 ? 0 : -1,
    "aria-selected": toolbarCursor == 1 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "i",
    icon: "type-italic",
    title: "Italic"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[2],
    tabIndex: toolbarCursor == 2 ? 0 : -1,
    "aria-selected": toolbarCursor == 2 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "u",
    icon: "type-underline",
    title: "Underline"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[3],
    tabIndex: toolbarCursor == 3 ? 0 : -1,
    "aria-selected": toolbarCursor == 3 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "sup",
    icon: "type",
    title: "Superscript"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[4],
    tabIndex: toolbarCursor == 4 ? 0 : -1,
    "aria-selected": toolbarCursor == 4 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "sub",
    icon: "type",
    title: "Subscript"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[5],
    tabIndex: toolbarCursor == 5 ? 0 : -1,
    "aria-selected": toolbarCursor == 5 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "h1",
    icon: "type-h1",
    title: "Header 1"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[6],
    tabIndex: toolbarCursor == 6 ? 0 : -1,
    "aria-selected": toolbarCursor == 6 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "h2",
    icon: "type-h2",
    title: "Header 2"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[7],
    tabIndex: toolbarCursor == 7 ? 0 : -1,
    "aria-selected": toolbarCursor == 7 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "h3",
    icon: "type-h3",
    title: "Header 3"
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[8],
    tabIndex: toolbarCursor == 8 ? 0 : -1,
    "aria-selected": toolbarCursor == 8 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "div",
    icon: "columns",
    title: "2 Column",
    onClick: () => {
      ReactEditor.focus(editor);
      const [match] = CustomEditor.nodes(editor, {
        match: n => {
          var _n$props6, _n$props6$style;
          return n.tagName === 'div' && ((_n$props6 = n.props) === null || _n$props6 === void 0 ? void 0 : (_n$props6$style = _n$props6.style) === null || _n$props6$style === void 0 ? void 0 : _n$props6$style.columnCount) === 2;
        }
      });
      if (!!match) {
        Transforms.unwrapNodes(editor, {
          match: n => {
            var _n$props7, _n$props7$style;
            return n.tagName === 'div' && ((_n$props7 = n.props) === null || _n$props7 === void 0 ? void 0 : (_n$props7$style = _n$props7.style) === null || _n$props7$style === void 0 ? void 0 : _n$props7$style.columnCount) === 2;
          }
        }, {});
      } else {
        Transforms.wrapNodes(editor, new Element({
          tagName: 'div',
          props: {
            style: {
              columnCount: 2
            }
          }
        }), {
          mode: "highest"
        });
      }
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[9],
    tabIndex: toolbarCursor == 9 ? 0 : -1,
    "aria-selected": toolbarCursor == 9 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "p",
    icon: "text-indent-right",
    title: "Indent Right",
    onClick: () => {
      var _match$0$props, _match$0$props$style;
      ReactEditor.focus(editor);
      const [match] = CustomEditor.nodes(editor, {
        match: n => {
          var _n$props8, _n$props8$style;
          return n.tagName === 'p' && ((_n$props8 = n.props) === null || _n$props8 === void 0 ? void 0 : (_n$props8$style = _n$props8.style) === null || _n$props8$style === void 0 ? void 0 : _n$props8$style.paddingLeft);
        }
      });
      if (!!match && parseRem((_match$0$props = match[0].props) === null || _match$0$props === void 0 ? void 0 : (_match$0$props$style = _match$0$props.style) === null || _match$0$props$style === void 0 ? void 0 : _match$0$props$style.paddingLeft) > 0) {
        var _match$0$props2, _match$0$props2$style;
        Transforms.setNodes(editor, {
          props: {
            style: {
              paddingLeft: parseRem((_match$0$props2 = match[0].props) === null || _match$0$props2 === void 0 ? void 0 : (_match$0$props2$style = _match$0$props2.style) === null || _match$0$props2$style === void 0 ? void 0 : _match$0$props2$style.paddingLeft) - 1 + "rem"
            }
          }
        }, {
          match: n => {
            var _n$props9, _n$props9$style;
            return n.tagName === 'p' && ((_n$props9 = n.props) === null || _n$props9 === void 0 ? void 0 : (_n$props9$style = _n$props9.style) === null || _n$props9$style === void 0 ? void 0 : _n$props9$style.paddingLeft);
          }
        });
      }
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[10],
    tabIndex: toolbarCursor == 10 ? 0 : -1,
    "aria-selected": toolbarCursor == 10 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "p",
    icon: "text-indent-left",
    title: "Indent Left",
    onClick: () => {
      ReactEditor.focus(editor);
      const [match] = CustomEditor.nodes(editor, {
        match: n => {
          var _n$props10, _n$props10$style;
          return n.tagName === 'p' && ((_n$props10 = n.props) === null || _n$props10 === void 0 ? void 0 : (_n$props10$style = _n$props10.style) === null || _n$props10$style === void 0 ? void 0 : _n$props10$style.paddingLeft);
        }
      });
      if (!!match) {
        var _match$0$props3, _match$0$props3$style, _match$0$props4, _match$0$props4$style;
        Transforms.setNodes(editor, {
          props: {
            style: {
              paddingLeft: parseRem((_match$0$props3 = match[0].props) === null || _match$0$props3 === void 0 ? void 0 : (_match$0$props3$style = _match$0$props3.style) === null || _match$0$props3$style === void 0 ? void 0 : _match$0$props3$style.paddingLeft) + 1 + "rem"
            }
          }
        }, {
          match: n => {
            var _n$props11, _n$props11$style;
            return n.tagName === 'p' && ((_n$props11 = n.props) === null || _n$props11 === void 0 ? void 0 : (_n$props11$style = _n$props11.style) === null || _n$props11$style === void 0 ? void 0 : _n$props11$style.paddingLeft);
          }
        });
        console.log(parseRem((_match$0$props4 = match[0].props) === null || _match$0$props4 === void 0 ? void 0 : (_match$0$props4$style = _match$0$props4.style) === null || _match$0$props4$style === void 0 ? void 0 : _match$0$props4$style.paddingLeft) + 1 + "rem");
      } else {
        Transforms.wrapNodes(editor, new Element({
          tagName: 'p',
          props: {
            style: {
              paddingLeft: '1rem'
            }
          }
        }), {});
      }
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[11],
    tabIndex: toolbarCursor == 11 ? 0 : -1,
    "aria-selected": toolbarCursor == 11 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "ol",
    icon: "list-ol",
    title: "Ordered List",
    onClick: () => {
      ReactEditor.focus(editor);
      Transforms.insertNodes(editor, [{
        type: 'Element',
        tagName: 'ol',
        children: [{
          type: 'Element',
          tagName: 'li',
          children: [{
            text: ' '
          }]
        }]
      }, {
        type: 'Element',
        tagName: 'p',
        children: [{
          text: ''
        }]
      }]);
      CustomEditor.moveSelection(editor, 2);
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[12],
    tabIndex: toolbarCursor == 12 ? 0 : -1,
    "aria-selected": toolbarCursor == 12 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "ul",
    icon: "list-ul",
    title: "Unordered List",
    onClick: () => {
      ReactEditor.focus(editor);
      Transforms.insertNodes(editor, [{
        type: 'Element',
        tagName: 'ul',
        children: [{
          type: 'Element',
          tagName: 'li',
          children: [{
            text: ' '
          }]
        }]
      }, {
        type: 'Element',
        tagName: 'p',
        children: [{
          text: ''
        }]
      }]);
      CustomEditor.moveSelection(editor, 2);
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[13],
    tabIndex: toolbarCursor == 13 ? 0 : -1,
    "aria-selected": toolbarCursor == 13 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "div",
    icon: "justify-left",
    title: "Justify Left",
    onClick: () => {
      alignText("left");
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[14],
    tabIndex: toolbarCursor == 14 ? 0 : -1,
    "aria-selected": toolbarCursor == 14 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "div",
    icon: "text-center",
    title: "Center Text",
    onClick: () => {
      alignText("center");
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[15],
    tabIndex: toolbarCursor == 15 ? 0 : -1,
    "aria-selected": toolbarCursor == 15 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "div",
    icon: "justify-right",
    title: "Justify Right",
    onClick: () => {
      alignText("right");
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[16],
    tabIndex: toolbarCursor == 16 ? 0 : -1,
    "aria-selected": toolbarCursor == 16 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "span",
    icon: "capslock",
    title: "Small Caps",
    onClick: () => {
      applyFontVariant("small-caps");
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[17],
    tabIndex: toolbarCursor == 17 ? 0 : -1,
    "aria-selected": toolbarCursor == 17 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "span",
    icon: "x-octagon-fill",
    title: "Disable Break",
    isActive: () => {
      const [match] = CustomEditor.nodes(editor, {
        match: n => {
          var _n$props12, _n$props12$style;
          return n.tagName === 'span' && ((_n$props12 = n.props) === null || _n$props12 === void 0 ? void 0 : (_n$props12$style = _n$props12.style) === null || _n$props12$style === void 0 ? void 0 : _n$props12$style.whiteSpace) === 'nowrap';
        }
      });
      return !!match;
    },
    onClick: () => {
      ReactEditor.focus(editor);
      const [match] = CustomEditor.nodes(editor, {
        match: n => {
          var _n$props13, _n$props13$style;
          return n.tagName === 'span' && ((_n$props13 = n.props) === null || _n$props13 === void 0 ? void 0 : (_n$props13$style = _n$props13.style) === null || _n$props13$style === void 0 ? void 0 : _n$props13$style.whiteSpace) === 'nowrap';
        }
      });
      if (!!match) {
        Transforms.unwrapNodes(editor, {
          match: n => {
            var _n$props14, _n$props14$style;
            return n.tagName === 'span' && ((_n$props14 = n.props) === null || _n$props14 === void 0 ? void 0 : (_n$props14$style = _n$props14.style) === null || _n$props14$style === void 0 ? void 0 : _n$props14$style.whiteSpace) === 'nowrap';
          }
        }, {});
      } else {
        Transforms.wrapNodes(editor, new Element({
          tagName: 'span',
          props: {
            style: {
              whiteSpace: 'nowrap'
            }
          }
        }), {
          split: true
        });
      }
    }
  }), /*#__PURE__*/React.createElement(ToolbarButton, {
    editor: editor,
    theRef: toolbarRefs.current[18],
    tabIndex: toolbarCursor == 18 ? 0 : -1,
    "aria-selected": toolbarCursor == 18 ? true : false,
    role: "button",
    inlineOnly: props.inlineOnly,
    tagName: "div",
    icon: "infinity",
    title: "Insert Equation",
    onClick: () => {
      handleShow();
    }
  }), (_props$toolbar2 = props.toolbar) === null || _props$toolbar2 === void 0 ? void 0 : _props$toolbar2.map((item, i) => {
    return /*#__PURE__*/React.createElement(ToolbarButton, _extends({
      editor: editor,
      theRef: toolbarRefs.current[19 + i],
      tabIndex: toolbarCursor == 19 + i ? 0 : -1,
      "aria-selected": toolbarCursor == 19 + i ? true : false,
      role: "button",
      inlineOnly: props.inlineOnly
    }, item));
  }), /*#__PURE__*/React.createElement(DebugButton, {
    editor: editor,
    theRef: toolbarRefs.current[19 + (((_props$toolbar3 = props.toolbar) === null || _props$toolbar3 === void 0 ? void 0 : _props$toolbar3.length) || 0)],
    tabIndex: toolbarCursor == 19 + (((_props$toolbar4 = props.toolbar) === null || _props$toolbar4 === void 0 ? void 0 : _props$toolbar4.length) || 0) ? 0 : -1,
    "aria-selected": toolbarCursor == 19 + (((_props$toolbar5 = props.toolbar) === null || _props$toolbar5 === void 0 ? void 0 : _props$toolbar5.length) || 0) ? true : false,
    role: "button",
    propValue: propValue
  })), /*#__PURE__*/React.createElement(Editable, {
    renderElement: p => {
      return CustomEditor.renderElement(editor, {
        onClickNonEditable: props.onClickNonEditable,
        ...p
      });
    },
    renderLeaf: CustomEditor.renderLeaf,
    onKeyDown: event => {
      if (event.ctrlKey) {
        switch (event.key) {
          case 'b':
          case 'i':
          case 'u':
            {
              event.preventDefault();
              CustomEditor.toggleTag(editor, event.key);
              break;
            }
          case 'Enter':
            {
              event.preventDefault();
              CustomEditor.insertText(editor, "\n​"); // zero-width space
            }

          default:
        }
      } else if (event.shiftKey) {
        if (event.key === 'Enter') {
          event.preventDefault();
          CustomEditor.insertText(editor, "\n​"); // zero-width space
        }
      }
    },

    onSelect: e => {
      if (!editor.selection) return;
      const isCollapsed = Range.isCollapsed(editor.selection);
      if (props.toolbar) {
        for (const item of props.toolbar) {
          const result = CustomEditor.getActiveTag(editor, item.tagName);
          if (!!result) {
            const [active, path] = result;
            DEBUGALL && console.log("RichText toolbar select", item.tagName, active);
            item === null || item === void 0 ? void 0 : item.onSelect(active, path);
          } else {
            item === null || item === void 0 ? void 0 : item.onSelect();
          }
        }
      }
      if (props.onSelect) {
        props.onSelect(e);
      }
      DEBUGALL && console.log("slate onSelect", editor.selection, isCollapsed);
    }
  })), /*#__PURE__*/React.createElement(MathJaxModal, {
    handleClose: handleClose,
    showModal: showModal,
    setShowModal: setShowModal,
    handleModalSave: handleModalSave
  })));
}